import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react';

import styles from './VerificationCodeInput.module.less';

type VerificationCodeInputProps = {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
};

/**
 * Imperative handle exposed for parents that need to drive focus — e.g. resetting
 *  to the first box after a wrong code is cleared.
 */
export type VerificationCodeInputHandle = {
  focusFirst: () => void;
};

/**
 * Input component for entering verification codes with individual digit boxes.
 * Supports auto-focus to next box, backspace navigation, and paste.
 */
const VerificationCodeInput = forwardRef<VerificationCodeInputHandle, VerificationCodeInputProps>(({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
}, ref) => {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(0);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.padEnd(length, ' ').split('').slice(0, length);

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useImperativeHandle(ref, () => ({
    focusFirst: () => inputRefs.current[0]?.focus(),
  }), []);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    // Only allow digits
    const digit = event.target.value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    const newValue = newDigits.join('').replace(/\s/g, '');

    onChange(newValue);

    // Auto-focus next input if digit was entered
    if (digit && index < length - 1) {
      // Use setTimeout to ensure focus happens after React update
      setTimeout(() => {
        inputRefs.current[index + 1]?.focus();
      }, 0);
    }

    // Call onComplete if all digits are filled
    if (newValue.length === length && onComplete) {
      setTimeout(() => {
        onComplete(newValue);
      }, 100);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key !== 'Backspace') {
      return;
    }

    if (event.currentTarget.value && event.currentTarget.value.trim() !== '') {
      // Clear current digit and move to previous
      event.preventDefault();
      const newDigits = [...digits];
      newDigits[index] = ' ';
      const newValue = newDigits.join('').replace(/\s/g, '');
      onChange(newValue);
    }

    setTimeout(() => {
      inputRefs.current[index - 1]?.focus();
    }, 0);
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, length);
    onChange(pastedData);

    // Focus the next empty input or the last one
    const nextIndex = Math.min(pastedData.length, length - 1);
    inputRefs.current[nextIndex]?.focus();

    // Call onComplete if all digits are filled
    if (pastedData.length === length && onComplete) {
      setTimeout(() => {
        onComplete(pastedData);
      }, 100);
    }
  };

  const handleFocus = (index: number) => {
    setFocusedIndex(index);
    // Select the digit if it exists
    inputRefs.current[index]?.select();
  };

  const handleBlur = () => {
    setFocusedIndex(null);
  };

  return (
    <div className={styles.container}>
      {digits.map((digit, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          type="tel"
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          inputMode="numeric"
          maxLength={1}
          value={digit.trim()}
          onChange={(e) => handleInputChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          onBlur={handleBlur}
          className={`${styles.digitInput} ${focusedIndex === index ? styles.active : ''}`}
          disabled={disabled}
        />
      ))}
    </div>
  );
});

VerificationCodeInput.displayName = 'VerificationCodeInput';

export default VerificationCodeInput;
